// Apoio às telas do controle de compras por período (solicitação, validação,
// entregas e recebimentos). A regra de embalagem replica a do servidor
// (CompraEmbalagemCalculo) apenas para pré-visualizar o efeito de um ajuste;
// o valor gravado é sempre o calculado pelo servidor.

export const PERCENTUAL_EMBALAGEM_PADRAO = 75;

function parametrizacaoAtiva(state, tipo) {
  const parametrizacoes = state?.inquilino?.parametrizacoes;
  return (
    Array.isArray(parametrizacoes) &&
    parametrizacoes.some((item) => item?.tipo === tipo && item.ativo)
  );
}

// A contagem do estoque usa o mesmo controle por período, com outra tela para o centro.
export function contagemEstoqueAtiva(state) {
  return parametrizacaoAtiva(
    state,
    "COMPRASSOLICITACAOCOMPRAEDEFINICAOESTOQUE"
  );
}

export function controlePeriodoAtivo(state) {
  return (
    parametrizacaoAtiva(state, "COMPRASCONTROLESOLICITACOESPERIODO") ||
    contagemEstoqueAtiva(state)
  );
}

export function normalizarPercentual(percentual) {
  const valor = Number(percentual);
  if (!Number.isInteger(valor) || valor < 1 || valor > 100) {
    return PERCENTUAL_EMBALAGEM_PADRAO;
  }
  return valor;
}

export function embalagemValida(embalagem) {
  return Number.isInteger(embalagem) && embalagem > 0;
}

export function calcularEmbalagem(
  quantidadeValidada,
  embalagem,
  percentual,
  tratamentoAtivo,
  centroPadrao
) {
  const quantidade = Math.max(0, Number.parseInt(quantidadeValidada, 10) || 0);
  const pendente = !!tratamentoAtivo && !embalagemValida(embalagem);
  if (quantidade === 0) {
    return { entregaDireta: 0, fracionada: 0, efetiva: 0, pendente };
  }
  if (!tratamentoAtivo || centroPadrao || pendente) {
    return {
      entregaDireta: 0,
      fracionada: quantidade,
      efetiva: quantidade,
      pendente,
    };
  }
  const completas = Math.floor(quantidade / embalagem);
  const saldo = quantidade % embalagem;
  let entregaDireta = completas * embalagem;
  let fracionada = saldo;
  if (saldo === 0) {
    entregaDireta = quantidade;
    fracionada = 0;
  } else if (saldo * 100 >= normalizarPercentual(percentual) * embalagem) {
    entregaDireta = (completas + 1) * embalagem;
    fracionada = 0;
  }
  return {
    entregaDireta,
    fracionada,
    efetiva: entregaDireta + fracionada,
    pendente: false,
  };
}

export function compraCentroPadrao(fracionada, embalagem, tratamentoAtivo) {
  if (fracionada <= 0) {
    return 0;
  }
  if (!tratamentoAtivo || !embalagemValida(embalagem)) {
    return fracionada;
  }
  return Math.ceil(fracionada / embalagem) * embalagem;
}

export function competenciaAtual(data = new Date()) {
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  return `${data.getFullYear()}-${mes}-01`;
}

export function deslocarCompetencia(competencia, meses) {
  const [ano, mes] = String(competencia).split("-").map(Number);
  const data = new Date(ano, mes - 1 + meses, 1);
  return competenciaAtual(data);
}

export function gerarChaveIdempotencia() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

export function normalizarErros(error, mensagemPadrao, campo = "Compras") {
  const data = error?.response?.data;
  const errors = data?.errors;
  if (Array.isArray(errors) && errors.length) {
    return errors.map((item) => ({
      fieldName: item.fieldName || item.field || campo,
      message: item.message || mensagemPadrao,
    }));
  }
  return [{ fieldName: campo, message: data?.message || mensagemPadrao }];
}

// Em downloads (responseType blob) o corpo do erro chega como Blob.
export async function normalizarErrosDownload(error, mensagemPadrao) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const corpo = JSON.parse(await data.text());
      return normalizarErros({ response: { data: corpo } }, mensagemPadrao);
    } catch (e) {
      return normalizarErros(null, mensagemPadrao);
    }
  }
  return normalizarErros(error, mensagemPadrao);
}

export function conflitoDeEdicao(error) {
  return error?.response?.status === 409;
}

export function baixarArquivo(conteudo, nomeArquivo, tipo) {
  const blob =
    conteudo instanceof Blob ? conteudo : new Blob([conteudo], { type: tipo });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", nomeArquivo);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function abrirPdf(conteudo) {
  const blob =
    conteudo instanceof Blob
      ? conteudo
      : new Blob([conteudo], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function normalizarTexto(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

// Saldo de material a receber: o que falta para o estoque chegar à quantidade por mês.
export function saldoReceber(quantidadeMensal, estoque) {
  return Math.max(0, (Number(quantidadeMensal) || 0) - (Number(estoque) || 0));
}

// Data ISO (aaaa-mm-dd) como dd/mm, sem passar por fuso horário.
export function formatarDiaMes(data) {
  const [, mes, dia] = String(data || "").split("-");
  return dia && mes ? `${dia.slice(0, 2)}/${mes}` : "";
}

// Finalização da contagem pelo centro: fica no painel inicial e, se a empresa
// parametrizou os dias, só é liberada entre eles.
export function finalizacaoContagem(dados) {
  const inicio = dados?.finalizacaoInicio || null;
  const fim = dados?.finalizacaoFim || null;
  return {
    inicio,
    fim,
    restrita: !!(inicio && fim),
    disponivel: dados?.finalizacaoDisponivel !== false,
  };
}
